export enum StepStatus {
  INITIAL = 1,
  ALMOST_PROSPECT = 2,
  PROSPECT = 3,
  ALMOST_CUSTOMER = 4,
  CUSTOMER = 5
}

export enum InyovaGrowAccountStatus {
  INITIAL = 1,
  RISK_FILLED = 2,
  BANK_APPLICATION_SENT = 3,
  OPENED = 4,
  FUNDED = 5
}

export interface Customer {
  accounts: CustomerAccountItem[];
  temporary_accounts: CustomerAccountItem[];
  app_location: 'ch' | 'de';
  authentication_token?: string;
  created_at: string;
  email: string;
  email_confirmed: boolean;
  enabled_features: string[];
  first_name: string;
  has_crowd_investor_account: boolean;
  has_interest_account: boolean;
  id: number;
  interview_widget: boolean;
  inyova_id: number;
  inyova_pro: boolean;
  is_friend: boolean;
  is_test: boolean;
  language: string;
  last_name: string;
  lead_source: string;
  hide_personalisation: boolean;
  my_referral_customer?: string;
  otp_module: string;
  phone: string;
  provider: string;
  referral_code: string;
  saxo_bank_onboarding_done: boolean;
  saxo_data_renewal_expires_at: null | string;
  show_eve2_opt_in: null | boolean;
  show_interest_account_banner: boolean;
  show_greenness_survey: GreennessSurveyTypes;
  source_type_id: number;
  superhuman_visible: boolean;
  tag_manager: {
    first_time: boolean;
    origin: string;
  };
  test_version: string;
  total_current_amount: number;
  total_referrals: {
    is_friend?: boolean;
    referral_code?: string;
    free_feedays?: number;
    my_referral_customer?: string;
    referrallimit?: boolean;
  };
  track_id: string;
  uid: string;
  unverified_phone: string;
  verified_phone: string;
  qualification_step: QualificationSteps;
}

export interface CustomerAccountItem {
  email?: string;
  id: string;
  isCrowdinvestor?: boolean;
  isGrowAccount?: boolean;
  kind: '3b' | '3a' | 'kid' | 'ci' | 'grow';
  owner_name: string;
  account_currency: string;
  step_status?: number;
  enabled_features: string[];
}

export type AccountKind = 'grow' | '3a' | '3b' | 'kid';

export interface ApiAccount {
  id: string;
  currency: string;
  iban: string;
  initial_investment: number;
  minimum_investment: number;
  owner_name: string;
  step_status: number;
  stock_split: number;
  kind: AccountKind | 'ci';
  monthly_investment: number | null;
  investment_risk_consent: boolean;
  fees_consent: boolean;
  reporting_timestamp: string;
  portfolio_value: number;
  portfolio_value_chf: number | null;
  portfolio_value_eur: number | null;
  portfolio_value_eur_chf: number | null;
  combined_expected_return: number;
  inyova_and_custody_fees: number;
  product_costs: number;
  commitments_made?: boolean | null;
  eur_exchange_rate: number;
  current_amount: number;
  created_at: string;
  deposit_qr_code: string;
  deposit_this_year: number;
  customer_journey: CustomerJourneyFlags;
  is_wisher?: boolean;
  active: boolean;
  edit_mode: boolean;
  has_low_investment_balance: boolean | null;
  is_yova_yellow: boolean;
  inyova_fee: number | null;
  enabled_features: string[];
  max_contribution_level?: 'large' | 'small' | 'transfer_only';
  next_strategy_change_on: string | null;
  onboarding_step: number;
  ql_onboarding_step: number;
  tutorial_seen: boolean;
  on_hold: boolean;
  converted_after_deadline?: boolean;
  strategy_edit_mode_disabled_at: string | null;
  strategy_edit_mode_enabled_at: string | null;
  show_eve2_explanation_modal: boolean;
  risk_recheck_visible: boolean;
  savings_plan_social_proof: SavingsPlanSocialProof | null;
  liberty_account: {
    qr_iban?: string;
    reference_number?: string;
  } | null;
  liquidation_only?: null | boolean;
}

export interface CustomerAccount extends Omit<
  ApiAccount,
  | 'monthly_investment'
  | 'investment_risk_consent'
  | 'fees_consent'
  | 'reporting_timestamp'
  | 'portfolio_value'
  | 'portfolio_value_chf'
  | 'portfolio_value_eur'
  | 'portfolio_value_eur_chf'
  | 'combined_expected_return'
  | 'inyova_and_custody_fees'
  | 'product_costs'
  | 'commitments_made'
  | 'eur_exchange_rate'
  | 'customer_id'
> {
  kind: Exclude<ApiAccount['kind'], 'grow'>;
}

export interface GrowAccount extends Omit<
  ApiAccount,
  | 'deposit_this_year'
  | 'active'
  | 'edit_mode'
  | 'first_time_yellow'
  | 'has_low_investment_balance'
  | 'is_yova_yellow'
  | 'inyova_fee'
  | 'max_contribution_level'
  | 'next_strategy_change_on'
  | 'tutorial_seen'
  | 'on_hold'
  | 'converted_after_deadline'
  | 'strategy_edit_mode_disabled_at'
  | 'strategy_edit_mode_enabled_at'
  | 'show_eve2_explanation_modal'
  | 'risk_recheck_visible'
  | 'liberty_account'
  | 'liquidation_only'
> {
  kind: 'grow';
}

export type Account = GrowAccount | CustomerAccount;

export interface SavingsPlanSocialProof {
  average: number;
  above_average: number;
  below_average: number;
  percentage: number;
}

export interface IbanOption {
  title: string;
  desc: string;
  type: 'account' | 'new';
  value?: string;
}

export interface IBANItem {
  id: string;
  type: string;
  attributes: {
    bank: string;
    iban: string;
  };
}

export interface CustomerJourneyFlags {
  account_opened: boolean;
  account_funded: boolean;
  id_verification_completed: boolean;
  customer_funded: boolean;
  compliance_done: boolean;
  account_data_filled: boolean;
  ql_flow_completed: boolean;
  risk_checked?: boolean;
}

export interface BankAccountDetail {
  bank: string;
  bic: string;
  address: string;
  city: string;
  country: string;
  zip: string;
  error?: string;
  errors?: string;
}

export interface ApiConfig {
  url: string | null;
  urlV3: string | null;
  urlV4: string | null;
  accountID: string | null;
}

export interface FinancialSituationBankDetail {
  address: string;
  bank: string;
  bic: string;
  city: string;
  country: string;
  zip: string;
}

export interface AccountInvestDetails {
  appLocation: 'ch' | 'de';
  currency?: string;
  iban: string;
  depositQrCode: string;
  isPossibleToCreate3A?: boolean;
  kind: '3b' | '3a' | 'kid' | 'ci' | 'interest' | 'grow';
  libertyAccount?: {
    qrIban?: string;
    referenceNumber?: string;
  };
  financialDetails?: {
    iban?: string;
    bank?: FinancialSituationBankDetail;
  };
  kidName?: string;
  ownerName: string;
  stillPossibleToInvest?: number;
}

export enum TransactionType {
  DEPOSIT = 'DEPOSIT',
  BUY = 'BUY',
  COUPON = 'COUPON',
  REPAYMENT = 'REPAYMENT',
  FEE = 'FEE',
  REIMBURSEMENT = 'REIMBURSEMENT',
  WITHDRAWAL = 'WITHDRAWAL',
  MONEY_PROMOTION = 'MONEY_PROMOTION',
  SELL = 'SELL'
}

export interface Transaction {
  transaction_date: string;
  transaction_type: TransactionType;
  transaction_value: number;
  type_translated: string;
  accrued_interest_at_purchase?: number;
}

export enum TransactionsRangeFilters {
  ALL = 'all',
  LAST_7_DAYS = 'sevenDays',
  LAST_MONTH = 'lastMonth',
  LAST_3_MONTHS = 'threeMonths',
  LAST_6_MONTHS = 'sixMonths',
  LAST_YEAR = 'lastYear',
  CUSTOM_RANGE = 'customRange'
}

export enum TransactionsTypeFilters {
  ALL = 'all',
  BUY = 'buy',
  DEPOSITS = 'deposit',
  WITHDRAWALS = 'withdrawal',
  FEES = 'fee',
  REIMBURSEMENTS = 'reimbursement',
  REPAYMENT = 'repayment',
  INTEREST = 'coupon',
  MONEY_PROMOTIONS = 'money_promotion',
  SELL = 'sell'
}

export const transactionsRangeDays = {
  [TransactionsRangeFilters.LAST_7_DAYS]: 7,
  [TransactionsRangeFilters.LAST_MONTH]: 30,
  [TransactionsRangeFilters.LAST_3_MONTHS]: 90,
  [TransactionsRangeFilters.LAST_6_MONTHS]: 180,
  [TransactionsRangeFilters.LAST_YEAR]: 365
};

export interface AccountTransactions {
  currency: string;
  loadState: LoadState;
  transactions: Transaction[];
  transactionsTotalLength: number;
  kind: AccountKind;
  kidName?: string;
  rangeFilter: TransactionsRangeFilters;
  selectedTypeFilters: TransactionsTypeFilters[];
  hasActiveFilter: boolean;
  customRange: { from: null | string; to: null | string };
  summaryData: { [key: string]: { sum: number; transactionsCount: number; accruedInterest?: number } };
  showInvite: boolean;
  invitePosition: null | number;
  totalSum: number;
  showReferralModal: boolean;
}

export class CustomError extends Error {
  status: number;
  error: { code: number };
  constructor(message: string, code: number) {
    super(message);
    this.status = code;
    this.error = { code };
  }
}

export const GREENNESS_INCOME_OPTIONS: { caption: string; value: string }[] = [
  {
    caption: '< 100000',
    value: '<100k'
  },
  {
    caption: '100000 - 250000',
    value: '100k-250k'
  },
  {
    caption: '250000 - 500000',
    value: '250k-500k'
  },
  {
    caption: '> 500000',
    value: '>500k'
  }
];

export const GREENNESS_NET_WORTH_OPTIONS: { caption: string; value: string }[] = [
  {
    caption: '< 50000',
    value: '<50k'
  },
  {
    caption: '50000 - 250000',
    value: '50k-250k'
  },
  {
    caption: '250000 - 500000',
    value: '250k-500k'
  },
  {
    caption: '500000 - 1000000',
    value: '500k-1m'
  },
  {
    caption: '1000000 - 5000000',
    value: '1m-5m'
  },
  {
    caption: '5000000 - 10000000',
    value: '5m-10m'
  },
  {
    caption: '> 10000000',
    value: '>10m'
  }
];

export type GreennessSurveyTypes = null | 'greenness' | 'greenness_and_finance' | 'finance';

export enum FeatureFlags {
  EVE2_3B = 'eve2_for_new_3b_kid_accounts_ch',
  EVE2 = 'eve2',
  EVE2_BANNER = 'eve2_banner',
  EVE2_3A_BANNER = 'eve2_3a_banner',
  AUTOIDENT = 'autoident',
  IMPACT2 = 'impact2',
  INVEST_DETAILS = 'invest_details',
  STRATEGY_HIDDEN_EVE2_MIGRATION = 'strategy_hidden_eve2_migration',
  GROW_CHF300_CAMPAIGN = 'grow_chf300_campaign',
  PORTFOLIO_UPDATES = 'portfolio_updates'
}

export type LoadState = 'INIT' | 'LOADING' | 'LOADED' | { error: string };

export enum Eve2DEDocumentId {
  EN = 'Y3VzdG9tZXJfc3RhdGljX2RvY3VtZW50cy9oaWRkZW4vY29udHJhY3RfYW1lbmRtZW50XzIwMjVfZW4ucGRm',
  DE = 'Y3VzdG9tZXJfc3RhdGljX2RvY3VtZW50cy9oaWRkZW4vY29udHJhY3RfYW1lbmRtZW50XzIwMjVfZGUucGRm'
}

export const eve2BannerLinks = {
  ch: {
    'en-CH': 'https://inyova.ch/en/your-new-inyova-strategy/',
    'de-CH': 'https://inyova.ch/deine-neue-inyova-strategie/',
    'fr-CH': 'https://inyova.ch/fr/ta-nouvelle-strategie-inyova-en-un-coup-doeil/'
  },
  de: {
    'en-DE': 'https://inyova.de/en/your-new-inyova-strategy/',
    'de-DE': 'https://inyova.de/deine-neue-inyova-strategie/'
  }
};

export type QualificationSteps = 'impact' | 'experience' | 'wealth' | 'investment_horizon' | 'completed';

export enum ViewItems {
  GROW_FIRST_EARNING_INVITE = 'grow_first_earning_invite'
}

export interface RecaptchaEnterpriseParams {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
}
